import { DocumentTextIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import type { PatientNote } from '../../../services/patient/notes';
import { NoteCard } from './NoteCard';
import { PinIcon } from './PinIcon';

interface NotesListProps {
    notes: PatientNote[];
    isLoading: boolean;
    /** Whether an active search query narrowed `notes` — picks the right
     *  empty state ("no matches" vs. "no notes yet"). */
    isSearching: boolean;
    onEdit: (note: PatientNote) => void;
    onDelete: (note: PatientNote) => void;
    onPin: (note: PatientNote) => void;
}

const MICRO_LABEL_CLASSES =
    'text-[10px] uppercase tracking-wider font-bold font-inter text-gray-400';

/**
 * Read view of a patient's notes. Notes are rendered in the exact order the
 * server returns them (pinned -> severity -> updatedAt desc) — never
 * re-sorted client-side. Pinned notes are grouped above the rest, preserving
 * each group's relative server order. A group header only renders while its
 * group has at least one (possibly search-filtered) note.
 */
function NotesList({ notes, isLoading, isSearching, onEdit, onDelete, onPin }: NotesListProps) {
    if (isLoading) {
        return (
            <div className="flex flex-col gap-3">
                {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="h-20 rounded-xl bg-gray-200 animate-pulse" />
                ))}
            </div>
        );
    }

    if (notes.length === 0 && isSearching) {
        return (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-400 py-12">
                <MagnifyingGlassIcon className="w-10 h-10 opacity-30" />
                <span className="text-sm font-inter">No notes match your search</span>
            </div>
        );
    }

    if (notes.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-400 py-12">
                <DocumentTextIcon className="w-10 h-10 opacity-30" />
                <span className="text-sm font-inter">No notes yet</span>
            </div>
        );
    }

    const pinnedNotes = notes.filter((note) => note.pinned);
    const unpinnedNotes = notes.filter((note) => !note.pinned);

    const renderCard = (note: PatientNote) => (
        <NoteCard key={note.id} note={note} onEdit={onEdit} onDelete={onDelete} onPin={onPin} />
    );

    return (
        <div className="flex flex-col gap-5">
            {pinnedNotes.length > 0 && (
                <div className="flex flex-col gap-3">
                    <div className={`flex items-center gap-1.5 ${MICRO_LABEL_CLASSES}`}>
                        <PinIcon className="w-3 h-3" />
                        Pinned
                    </div>
                    <div className="flex flex-col gap-3">{pinnedNotes.map(renderCard)}</div>
                </div>
            )}

            {unpinnedNotes.length > 0 && (
                <div className="flex flex-col gap-3">
                    {/* The "Other notes" label only earns its place when a pinned
                        group sits above it — as the only group, it needs no name. */}
                    {pinnedNotes.length > 0 && (
                        <div className={MICRO_LABEL_CLASSES}>Other notes</div>
                    )}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {unpinnedNotes.map(renderCard)}
                    </div>
                </div>
            )}
        </div>
    );
}

export { NotesList };
